import React, { useMemo, useState, useCallback, useEffect } from "react";
import { subDays, startOfDay, endOfDay } from "date-fns";
import { TZDate } from "@date-fns/tz";
import { TimeInterval } from "@/types";
import { useGlobalStore } from "@/GlobalStore";
import SubmenuDropdown, {
  SubmenuOption,
} from "@/components/common/dropdown/SubmenuDropdown";
import DateRangePicker from "@/components/common/calendar/DateRangePicker";
import type { DateRangeWithLabel } from "@/utils/dateUtils";
import { calcZonedDate, computeAutoCompareRange } from "@/utils/dateUtils";
import { RiCalendarLine } from "@remixicon/react";
import { formatDateRange } from "@/utils/dateUtils";

type SupportedPresets =
  | TimeInterval.LAST_7_DAYS
  | TimeInterval.LAST_14_DAYS
  | TimeInterval.LAST_28_DAYS
  | TimeInterval.LAST_30_DAYS;

const computeCompareValue = (label: TimeInterval, from: Date, to: Date, tz: string) => {
  const main = { label, from, to } as DateRangeWithLabel;
  const cmp = computeAutoCompareRange(main, tz);
  return cmp ? { startDate: cmp.from, endDate: cmp.to } : null;
};

export const getPresetDateRanges = (
  timezone: string,
): Record<SupportedPresets, DateRangeOption> => {
  const now = new Date();
  const tz = timezone;
  const endDate = calcZonedDate(now, tz, endOfDay);
  const presets: Record<SupportedPresets, DateRangeOption> = {
    [TimeInterval.LAST_7_DAYS]: {
      key: "7d",
      label: TimeInterval.LAST_7_DAYS,
      value: {
        startDate: calcZonedDate(calcZonedDate(now, tz, subDays, 6), tz, startOfDay),
        endDate,
      },
      compareValue: computeCompareValue(
        TimeInterval.LAST_7_DAYS,
        calcZonedDate(calcZonedDate(now, tz, subDays, 6), tz, startOfDay),
        endDate,
        tz,
      ),
    },
    [TimeInterval.LAST_14_DAYS]: {
      key: "14d",
      label: TimeInterval.LAST_14_DAYS,
      value: {
        startDate: calcZonedDate(calcZonedDate(now, tz, subDays, 13), tz, startOfDay),
        endDate,
      },
      compareValue: computeCompareValue(
        TimeInterval.LAST_14_DAYS,
        calcZonedDate(calcZonedDate(now, tz, subDays, 13), tz, startOfDay),
        endDate,
        tz,
      ),
    },
    [TimeInterval.LAST_28_DAYS]: {
      key: "28d",
      label: TimeInterval.LAST_28_DAYS,
      value: {
        startDate: calcZonedDate(calcZonedDate(now, tz, subDays, 27), tz, startOfDay),
        endDate,
      },
      compareValue: computeCompareValue(
        TimeInterval.LAST_28_DAYS,
        calcZonedDate(calcZonedDate(now, tz, subDays, 27), tz, startOfDay),
        endDate,
        tz,
      ),
    },
    [TimeInterval.LAST_30_DAYS]: {
      key: "30d",
      label: TimeInterval.LAST_30_DAYS,
      value: {
        startDate: calcZonedDate(calcZonedDate(now, tz, subDays, 29), tz, startOfDay),
        endDate,
      },
      compareValue: computeCompareValue(
        TimeInterval.LAST_30_DAYS,
        calcZonedDate(calcZonedDate(now, tz, subDays, 29), tz, startOfDay),
        endDate,
        tz,
      ),
    },
  };
  return presets;
};

export type DateRangePair = {
  startDate: Date;
  endDate: Date;
};

export type DateRangeOption = {
  key: string;
  label: TimeInterval;
  value: DateRangePair;
  compareValue?: DateRangePair | null;
};

export type DateRangeDropdownProps = {
  /** The currently selected date range */
  selectedRange?: DateRangeOption | null;
  /** Callback function called when a date range is selected */
  onRangeChange?: (range: DateRangeOption) => void;
  /** Additional CSS classes for styling */
  className?: string;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Position of the label relative to the selected option */
  labelPosition?: "left" | "right";
  allowedDateRange: {
    first_allowed_date: TZDate;
    last_allowed_date: TZDate;
  } | null;
};

/**
 * A date range dropdown component that provides predefined date range options.
 *
 * This component displays a dropdown with options like "Last 7 Days", "Last 14 Days",
 * and "Last 28 Days", along with the actual date range in parentheses.
 *
 * @example
 * ```tsx
 * import DateRangeDropdown from "@/components/Overview/dropdowns/DateRangeDropdown";
 *
 * // Basic usage
 * <DateRangeDropdown
 *   onRangeChange={(range) => console.log('Selected range:', range)}
 * />
 *
 * // With custom props
 * <DateRangeDropdown
 *   selectedRange={customRange}
 *   onRangeChange={handleRangeChange}
 *   className="w-64"
 *   labelPosition="right"
 *   disabled={false}
 * />
 * ```
 */
const DateRangeDropdown: React.FC<DateRangeDropdownProps> = ({
  selectedRange,
  onRangeChange,
  className,
  disabled = false,
  labelPosition = "right",
  allowedDateRange,
}) => {
  const { selectedAccount } = useGlobalStore();
  const timeZone = selectedAccount?.time_zone || "UTC";
  const [customValue, setCustomValue] = useState<DateRangeOption | null>(null);
  const [tempSelectedDate, setTempSelectedDate] = useState<DateRangeWithLabel | null>(
    null,
  );
  const [tempCompareDate, setTempCompareDate] = useState<DateRangeWithLabel | null>(null);

  // Generate date range options with useMemo to prevent recreation on every render
  const dateRangeOptions = useMemo((): DateRangeOption[] => {
    const presets = getPresetDateRanges(timeZone);
    return [
      presets[TimeInterval.LAST_7_DAYS],
      presets[TimeInterval.LAST_14_DAYS],
      presets[TimeInterval.LAST_28_DAYS],
      presets[TimeInterval.LAST_30_DAYS],
    ];
  }, [timeZone]);

  // Helper function to check if a date range is within allowed bounds
  const isDateRangeWithinAllowed = useCallback(
    (startDate: Date, endDate: Date): boolean => {
      if (!allowedDateRange) return true;

      const firstAllowed = allowedDateRange.first_allowed_date;
      const lastAllowed = allowedDateRange.last_allowed_date;

      // Use getTime() for reliable date comparisons
      // TZDate extends Date, so getTime() works directly
      const firstAllowedTime = firstAllowed.getTime();
      const lastAllowedTime = lastAllowed.getTime();
      const startTime = startDate.getTime();
      const endTime = endDate.getTime();

      // Check if the range falls within allowed bounds
      // Start date should be >= first_allowed_date
      // End date should be <= last_allowed_date
      return startTime >= firstAllowedTime && endTime <= lastAllowedTime;
    },
    [allowedDateRange],
  );

  // Build submenu options including Custom
  const options: SubmenuOption<DateRangeOption>[] = useMemo(() => {
    const base = dateRangeOptions.map((o) => ({
      key: o.key,
      label: o.label,
      value: o,
      disabled: !isDateRangeWithinAllowed(o.value.startDate, o.value.endDate),
    }));

    const customKey = "custom";
    const currentCustom: DateRangeOption = customValue || {
      key: customKey,
      label: TimeInterval.CUSTOM,
      value: {
        startDate: selectedRange?.value.startDate || dateRangeOptions[0].value.startDate,
        endDate: selectedRange?.value.endDate || dateRangeOptions[0].value.endDate,
      },
    };

    const customOption: SubmenuOption<DateRangeOption> = {
      key: customKey,
      label: TimeInterval.CUSTOM,
      value: currentCustom,
      renderSubmenu: ({ close, select }) => {
        const selectedForPicker: DateRangeWithLabel | null = tempSelectedDate
          ? tempSelectedDate
          : {
              label: currentCustom.label,
              from: currentCustom.value.startDate,
              to: currentCustom.value.endDate,
            };

        // If parent hasn't provided a compare, compute one to display by default
        const compareForPicker: DateRangeWithLabel | null =
          tempCompareDate ||
          computeAutoCompareRange(
            selectedForPicker,
            timeZone,
            allowedDateRange?.first_allowed_date,
          );

        return (
          <div className="rounded-[12px] overflow-hidden">
            <DateRangePicker
              selected={selectedForPicker}
              compareDate={compareForPicker || undefined}
              onSelect={(range) => {
                if (range) {
                  const newRange = {
                    from: new TZDate(range.from, timeZone),
                    to: new TZDate(range.to, timeZone),
                    label: range.label || TimeInterval.CUSTOM,
                  };
                  setTempSelectedDate(newRange);
                }
              }}
              onCompareDateChange={(range) => {
                if (range) {
                  const newRange = {
                    from: new TZDate(range.from, timeZone),
                    to: new TZDate(range.to, timeZone),
                    label: range.label || TimeInterval.CUSTOM,
                  };
                  setTempCompareDate(newRange);
                } else {
                  setTempCompareDate(null);
                }
              }}
              dateAggregate={""}
              defaultMonth={currentCustom.value.endDate}
              showSidebar={false}
              onApply={(range, _agg, _chunks, compareRange) => {
                if (range) {
                  setTempSelectedDate({
                    from: range.from,
                    to: range.to,
                    label: range.label,
                  });
                  setTempCompareDate(
                    compareRange
                      ? {
                          from: compareRange.from,
                          to: compareRange.to,
                          label: compareRange.label,
                        }
                      : null,
                  );
                  const next: DateRangeOption = {
                    key: customKey,
                    label: TimeInterval.CUSTOM,
                    value: { startDate: range.from, endDate: range.to },
                    compareValue: compareRange
                      ? { startDate: compareRange.from, endDate: compareRange.to }
                      : null,
                  };
                  setCustomValue(next);
                  onRangeChange?.(next);
                  select(next);
                }
                close();
              }}
              autoAllowed={false}
              showAggregate={false}
              timezone={timeZone}
              enableCompare={true}
              hideCancelButton={true}
              first_allowed_date={allowedDateRange?.first_allowed_date}
              last_allowed_date={allowedDateRange?.last_allowed_date}
            />
          </div>
        );
      },
    };

    return [...base, customOption];
  }, [
    dateRangeOptions,
    customValue,
    onRangeChange,
    timeZone,
    tempSelectedDate,
    tempCompareDate,
    allowedDateRange,
    selectedRange,
    isDateRangeWithinAllowed,
  ]);

  // No need for this effect anymore - parent controls the state

  const handleChange = useCallback(
    (option: DateRangeOption) => {
      // Keep temp state in sync so the Custom picker opens with current selection
      setTempSelectedDate({
        from: option.value.startDate,
        to: option.value.endDate,
        label: option.label,
      });
      setTempCompareDate(
        option.compareValue
          ? {
              from: option.compareValue.startDate,
              to: option.compareValue.endDate,
              label: option.label,
            }
          : null,
      );
      onRangeChange?.(option);
    },
    [onRangeChange],
  );

  // Find the exact option from the options array to ensure proper reference equality
  const currentSelected = useMemo(() => {
    const fallback = dateRangeOptions[0];
    if (!selectedRange) return fallback;
    const found = options.find((opt) => opt.key === selectedRange.key)?.value;
    return found || fallback;
  }, [selectedRange, options, dateRangeOptions]);

  // Sync temp states with external selectedRange
  useEffect(() => {
    if (!selectedRange) return;
    setTempSelectedDate({
      from: selectedRange.value.startDate,
      to: selectedRange.value.endDate,
      label: selectedRange.label,
    });
    setTempCompareDate(
      selectedRange.compareValue
        ? {
            from: selectedRange.compareValue.startDate,
            to: selectedRange.compareValue.endDate,
            label: selectedRange.label,
          }
        : null,
    );
  }, [selectedRange]);

  const { dateRangeText, compareRangeText } = useMemo(() => {
    const tz = timeZone;

    const currentStr = formatDateRange({
      start_date: currentSelected.value.startDate,
      end_date: currentSelected.value.endDate,
      timezone: tz,
    });

    // If Custom and a compare exists (from parent or recent picker interaction), get compare text
    const comparePair = selectedRange?.compareValue
      ? {
          from: selectedRange.compareValue.startDate,
          to: selectedRange.compareValue.endDate,
        }
      : null;

    const compareStr =
      currentSelected.label === TimeInterval.CUSTOM && comparePair
        ? formatDateRange({
            start_date: comparePair.from,
            end_date: comparePair.to,
            timezone: tz,
          })
        : null;

    return {
      dateRangeText: currentStr,
      compareRangeText: compareStr,
    };
  }, [currentSelected, timeZone, selectedRange?.compareValue]);

  return (
    <SubmenuDropdown
      options={options}
      selected={currentSelected}
      onChange={handleChange}
      className={className}
      disabled={disabled}
      label={
        <span className="flex items-center gap-1">
          <span
            className={
              selectedRange?.label === TimeInterval.CUSTOM
                ? "text-brand-primary-800 font-medium"
                : "text-brand-primary-600 font-normal"
            }
          >
            {dateRangeText}
          </span>
          {compareRangeText && (
            <>
              {" "}
              <span className="text-brand-primary-400 font-normal">vs</span>
              <span className="text-brand-primary-800 font-normal">
                {compareRangeText}
              </span>
            </>
          )}
        </span>
      }
      labelIcon={RiCalendarLine}
      labelPosition={labelPosition}
      placeholder="Select date range"
      hideSelectedWhenKeyIn={["custom"]}
      onOpenChange={(isOpen) => {
        if (!isOpen && selectedRange) {
          // Reset temps to parent values on close when no Apply
          setTempSelectedDate({
            from: selectedRange.value.startDate,
            to: selectedRange.value.endDate,
            label: selectedRange.label,
          });
          setTempCompareDate(
            selectedRange.compareValue
              ? {
                  from: selectedRange.compareValue.startDate,
                  to: selectedRange.compareValue.endDate,
                  label: selectedRange.label,
                }
              : null,
          );
        }
      }}
    />
  );
};

export default DateRangeDropdown;
